import { User } from '@/interfaces/User';
import { eventBus } from '@/services/event-bus';
import apiUrls from '@/shared/api-urls';
import { constants } from '@/shared/constants';
import { getUrlOrSubscriber } from '@/shared/helpers';
import routes from '@/shared/routes';
import { urls } from '@/shared/urls';

import axiosInstance from './axios';
import storage from './storage';

interface UserService {
  userPromiseResolve: Function;
  userPromise: Promise<User> | null;
  userTokenPromiseResolve: Function;
  userTokenPromise: Promise<string> | null;
  init: Function;
  getUserData: Function;
  getUserToken: Function;
  refreshToken: Function;
  getUserProfile: Function;
  updateUser: Function;
  clearUserData: Function;
  logout: Function;
  getUserId(): string;
}

export default {
	userPromiseResolve: new Function,
	userPromise: null,
	userTokenPromiseResolve: new Function,
	userTokenPromise: null,

	init(): void {
		if (routes.isLoginPage() || routes.checkPage(urls.registration))
			storage.setItem(constants.storage.nav, 'false');

		this.userPromise = new Promise(resolve => this.userPromiseResolve = resolve);
		this.userTokenPromise = new Promise(resolve => this.userTokenPromiseResolve = resolve);

		const userState = storage.getItem(constants.storage.user);
		if (userState) {
			this.userPromiseResolve(userState);
			eventBus.$emit('user-updated', userState);
		}

		const userToken = storage.getItem(constants.storage.userToken);
		if (userToken)
			this.userTokenPromiseResolve(userToken);

		const nav = storage.getItem(constants.storage.nav) === 'false';

		if ((!routes.isSpecialPages(constants.notAuthPages) || nav) && (!userToken || !userState))
			this.getUserData();
	},
	getUserData(): void {
		return this.getUserToken()
			.then(() => this.getUserProfile()
				.then(() => {
					axiosInstance.get(apiUrls.getNavigation.url)
						.then(({ data }) => {
							if (data.length) {
								storage.removeItem(constants.storage.nav);
								(window as any).location = getUrlOrSubscriber(data);
							}
							else
								(window as any).location = urls.addService;
						});
				}));
	},
	getUserToken(): Promise<void> {
		return axiosInstance
			.post(apiUrls.getToken.url)
			.then(response => {
				if (response.data) {
					storage.setItem(constants.storage.userToken, response.data);
					this.userTokenPromiseResolve(response.data);
				}
			});
	},
	refreshToken(newToken: string): void {
		storage.setItem(constants.storage.userToken, newToken);
		this.userTokenPromise = Promise.resolve(newToken);
		this.getUserProfile();
	},
	getUserProfile(): Promise<User> {
		return axiosInstance.get(apiUrls.userProfileDetails.url)
			.then(response => {
				this.userPromiseResolve(response.data);
				this.updateUser(response.data);
				return response.data;
			});
	},
	updateUser(update: Partial<User>): void {
		const user = { ...(storage.getItem(constants.storage.user) || {}), ...update };
		storage.setItem(constants.storage.user, user);
		this.userPromise = Promise.resolve(user);
		eventBus.$emit('user-updated', user);
	},
	clearUserData(): void {
		storage.removeItem(constants.storage.user);
		storage.removeItem(constants.storage.userToken);
		storage.removeItem(constants.storage.nav);
		storage.removeItem(constants.storage.subscriberIdentity);
		storage.removeItem(constants.storage.userId);
		storage.removeItem(constants.storage.hasUnpaidBills);
	},
	logout(reload = true): Promise<void> {
		return axiosInstance.post(apiUrls.logout.url)
			.then(() => {
				this.clearUserData();

				if (reload)
					window.location.href = window.location.href.split('#')[0];
			});
	},
	getUserId(): string {
		return storage.getItem(constants.storage.userId);
	}
} as UserService;
